import { useGetLastAdQuery } from '../../../../api/affiliate/Affiliate.api'
import { LinksList } from '../../ad-overview/components/LinksList'
import { BpAnchor } from '../../../../shared/components/text/anchor/BpAnchor'
import React, { useEffect } from 'react'
import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { Preloader } from '../../../../shared/components/cards/preloader/Preloader'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks/Redux'
import { hideModal } from '../../../../store/reducers/AppSlice'
import { IconUpload } from '../../../../shared/components/icons/IconUpload'
import {
  useExportAffiliateAdsMutation,
  useExportLastAdMutation,
} from '../../../../api/export-reports/ExportReports.api'
import { IPpcComplianceFilter } from '../interfaces/PpcComplianceFilter.interface'
import { selectPpcComplianceFilter } from '../store/PpcComplianceSlice'
import { useTranslation } from 'react-i18next'

export interface IPpcComplianceLastAdProps {
  id: number
  projectId: string
  adsCount: number
  affiliate: string
}

export const PpcComplianceLastAd = ({
  id,
  projectId,
  adsCount,
  affiliate,
}: IPpcComplianceLastAdProps) => {
  const {
    project,
    country,
    network,
    dateRange,
    site,
    engine,
    browser,
    city,
    hidden,
    adHijackers,
    status,
  } = useAppSelector<IPpcComplianceFilter>(selectPpcComplianceFilter)
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const { data, isLoading, isFetching, isError } = useGetLastAdQuery({
    id,
    date: dateRange,
    project_id: projectId,
    is_hijack: adHijackers,
  })
  const [exportLastAd, { isLoading: lastAdLoading }] = useExportLastAdMutation()
  const [exportAffiliateAds, { isLoading: affiliateAdsLoading }] = useExportAffiliateAdsMutation()

  useEffect(() => {
    isError && dispatch(hideModal())
  }, [isError])

  if (isLoading || isFetching) {
    return (
      <div className='relative min-h-[200px]'>
        <Preloader />
      </div>
    )
  }

  if (!data?.data?.items.length) {
    return (
      <div className='flex py-[30px] flex-col items-center justify-center gap-[40px] h-full inset-0'>
        <svg
          fill='none'
          height='85'
          viewBox='0 0 78 85'
          width='78'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M30 77.0004L35.892 80.2736C37.0264 80.904 37.5936 81.2188 38.1944 81.3424C38.726 81.452 39.274 81.452 39.806 81.3424C40.4064 81.2188 40.9736 80.904 42.108 80.2736L48 77.0004M12 67.0004L6.29188 63.8292C5.09384 63.1636 4.49472 62.8308 4.05852 62.3572C3.67264 61.9388 3.3806 61.4424 3.20192 60.9016C3 60.2904 3 59.6052 3 58.2344V52.0004M3 32.0004V25.7661C3 24.3956 3 23.7103 3.20192 23.0991C3.3806 22.5584 3.67264 22.062 4.05852 21.6433C4.49472 21.1699 5.0938 20.8371 6.29188 20.1715L12 17.0004M30 7.00032L35.892 3.72708C37.0264 3.09684 37.5936 2.78172 38.1944 2.65816C38.726 2.54884 39.274 2.54884 39.806 2.65816C40.4064 2.78172 40.9736 3.09684 42.108 3.72708L48 7.00032M66 17.0003L71.708 20.1715C72.9064 20.8371 73.5052 21.1699 73.9416 21.6433C74.3272 22.062 74.6196 22.5584 74.798 23.0991C75 23.7103 75 24.3956 75 25.7661V32.0003M75 52.0004V58.2344C75 59.6052 75 60.2904 74.798 60.9016C74.6196 61.4424 74.3272 61.9388 73.9416 62.3572C73.5052 62.8308 72.9064 63.1636 71.708 63.8292L66 67.0004M30 37.0004L39 42.0004M39 42.0004L48 37.0004M39 42.0004V52.0004M3 22.0003L12 27.0003M66 27.0003L75 22.0003M39 72.0004V82.0004'
            stroke='#6684A7'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='5'
          />
        </svg>
        <span className='text-[24px] text-aqua-300 font-medium'>No Results Found</span>
      </div>
    )
  }

  return (
    <div className={'flex flex-col overflow-y-hidden'}>
      <h3
        className={
          'text-[20px] font-medium flex min-h-[55px] items-center px-[32px] w-full border-b-[3px] border-b-[#00326D]/[.02]'
        }
      >
        {t('pages.PPCCompliance.latestAdFromAffiliate')} {data?.data?.items[0].affiliate}
      </h3>
      <div className={'overflow-y-auto'}>
        <div className={'flex w-full px-[32px] pt-[16px] gap-x-[40px] flex-wrap md:flex-nowrap'}>
          <div className={'max-w-[440px] w-full'}>
            <h4 className={'text-[16px] leading-[32px] pb-[8px] font-medium'}>
              {t('pages.PPCCompliance.adCopy')}
            </h4>
            <div className={'bg-[#00326D05] rounded-[12px] p-[24px]'}>
              <p
                className={
                  'font-roboto text-[14px] leading-[16px] text-[#1A23FF] font-semibold pb-[2px]'
                }
              >
                {data?.data?.items[0].title}
              </p>
              <p className={'font-roboto text-[14px] leading-[18px]'}>
                {data?.data?.items[0].text}
              </p>
              <p className={'font-roboto text-[14px] leading-[18px] text-[#4DB11E] pt-[2px]'}>
                {data?.data?.items[0].finish_domain}
              </p>
            </div>
          </div>
          <div className={'max-w-[300px] w-full'}>
            <h4 className={'text-[16px] leading-[32px] pb-[8px] font-medium'}>
              {t('pages.PPCCompliance.adDetails')}
            </h4>
            <div className={'leading-[32px] text-[15px]'}>
              <div>
                <span className={'text-[#6684A7]'}>{t('table.columns.found')}:</span>{' '}
                {data?.data?.items[0].found}
              </div>
              <div>
                <span className={'text-[#6684A7]'}>{t('table.columns.searchEngine')}:</span>{' '}
                {data?.data?.items[0].engine}
              </div>
              <div>
                <span className={'text-[#6684A7]'}>{t('table.columns.keyword')}:</span>{' '}
                {data?.data?.items[0].keyword}
              </div>
              <div>
                <span className={'text-[#6684A7]'}>{t('table.columns.device')}:</span>{' '}
                {data?.data?.items[0].device}
              </div>
              <div className={'flex justify-between flex-wrap'}>
                <BpAnchor href={data?.data?.items[0].search_screen_url} target='_blank'>
                  {t('pages.PPCCompliance.SERPScreenshot')}
                </BpAnchor>
                <BpAnchor href={data?.data?.items[0].link_screen_url} target='_blank'>
                  {t('pages.PPCCompliance.landingPageScreenshot')}
                </BpAnchor>
              </div>
            </div>
          </div>
        </div>
        <div className={'w-full px-[16px] pt-[10px] pb-[16px]'}>
          <LinksList
            additionalRedirects={data?.data?.items[0].additionalRedirects}
            className='!shadow-none'
            redirects={data?.data?.items[0].redirects}
          />
          <p className={'px-[16px]'}>{affiliate}</p>
        </div>
      </div>
      <div
        className={
          'w-full flex gap-[12px] px-[32px] py-[24px] border-t-[3px] border-t-[#00326D]/[.02]'
        }
      >
        <BpButton
          className={'uppercase'}
          iconLeft={<IconUpload />}
          loading={lastAdLoading}
          onClick={() =>
            exportLastAd({ id, project_id: projectId, date: dateRange, is_hijack: adHijackers })
          }
        >
          {t('buttons.exportLatestAdToPDF')}
        </BpButton>
        <BpButton
          className={'uppercase'}
          iconLeft={<IconUpload />}
          loading={affiliateAdsLoading}
          onClick={() =>
            exportAffiliateAds({
              dateRange,
              project_id: project ? Number(project) : undefined,
              country_id: country ? Number(country) : undefined,
              affiliate,
              network: network,
              date: dateRange,
              site: site,
              engine_id: engine ? Number(engine) : undefined,
              browser_id: browser ? Number(browser) : undefined,
              city_id: city ? Number(city) : undefined,
              is_hidden: hidden,
              is_hijack: adHijackers,
              status_id: status ? Number(status) : undefined,
              export: 'xlsx',
            })
          }
        >
          {t('buttons.exportCountAdsToXls', { count: adsCount })}
        </BpButton>
      </div>
    </div>
  )
}
